<template>
  <div>
    <div v-if="hasPhotos" class="photo-gallery">
      <template v-for="(photo, index) in item.response">
        <DrawablePhotoV1OrV2
          v-if="isVisiblePhoto(photo)"
          :key="index"
          photo-class="mr-6 mb-3"
          :image="photo"
          :drawing="photo.drawing"
          :thumbnail="photo.thumbnail"
          :width="photo.width"
          :height="photo.height"
          :loading="photo.loading"
          :allow-drawing="allowDrawing"
          @change="updateDrawing(index, arguments[0])"
          @delete="deletePhoto(index)"
        />
      </template>
    </div>

    <div class="mt-2">
      <label v-if="canAddPhoto">
        <input
          class="hidden"
          type="file"
          accept="image/*"
          :disabled="loading"
          :multiple="item.params.allow_multiple"
          @change="addPhoto"
        >
        <span class="btn btn-default btn-primary main-button" :disabled="loading">
          <Loader v-if="loading" />
          <template v-else>
              <CameraIcon width="24" height="24" style="position: relative; top: -0.1rem;" fill="var(--white)"
                          class="mr-2"
              />
              {{ addPhotoButtonTitle }}
          </template>
        </span>
      </label>
      <div v-else-if="!allowGallerySelection" class="message is-info">
        <p class="message-body">
          {{ $t("inspections.gallery-restricted-message") }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
  import CameraIcon from '@/_legacy/components/Icons/CameraIcon.vue';
  import Loader from '@/_legacy/components/Icons/Loader.vue';
  import { eventBus } from '@/_legacy/eventBus';
  import { t } from '@/ui/i18n/i18n';
  import { DrawablePhotoV1OrV2 } from '@/_legacy/components/DrawablePhoto/DrawablePhotoV1OrV2';
  import PhotoReader from '@/_legacy/util/PhotoReader';
  import { AssetsPhotoActions, getPhotoStub } from '@/services/assets-api/AssetsPhotoService';
  import * as AssetsPhotoStateService from '@/services/assets-api/AssetsPhotoStateService';
  import * as R from 'ramda';

  const photoStubDictionary = [];

  export default {
    components: {
      DrawablePhotoV1OrV2,
      Loader,
      CameraIcon,
    },
    props: {
      item: {
        type: Object,
        required: true,
      },
    },
    data: () => ({
      loading: false,
      visiblePhotos: [],
    }),
    computed: {
      hasPhotos() {
        return this.visiblePhotos && this.visiblePhotos.length > 0;
      },
      addPhotoButtonTitle() {
        if (this.item.response && this.visiblePhotos.length > 0) {
          return this.$t('Take another photo');
        }
        return this.$t('Take photo');
      },
      // when can a user upload a photo
      canAddPhoto() {
        if (!this.allowGallerySelection) {
          return false;
        }

        if (this.item.params.allow_multiple) {
          return true;
        }

        return !this.item.response || this.visiblePhotos.length === 0;
      },
      allowDrawing() {
        if (this.item.params.allow_drawing === true) {
          return true;
        }
        return this.item.params.allow_drawing !== false;
      },
      allowGallerySelection() {
        return R.pathOr(true, ['params', 'allow_gallery_selection'], this.item);
      },
    },
    watch: {
      item: {
        handler(value) {
          if (!value.response) {
            return;
          }
          this.visiblePhotos = value.response.filter(photo => photo.action !== AssetsPhotoActions.DELETE_IMAGE);
          this.$forceUpdate();
        },
        deep: true,
        immediate: true,
      },
    },
    methods: {
      isVisiblePhoto(photo) {
        return photo.action !== AssetsPhotoActions.DELETE_IMAGE;
      },
      deletePhoto(index) {
        this.loading = true;

        const photoToDelete = this.item.response[index];

        AssetsPhotoStateService.deletePhoto(photoToDelete);

        this.item.response.splice(index, 1, photoToDelete);
        this.$set(this.item, 'response', [...this.item.response]);

        const responded = this.item.required ? this.item.response.some(photo => this.isVisiblePhoto(photo)) : this.item.response.length > 0;
        this.$set(this.item, 'responded', responded);

        this.loading = false;
      },
      removeDrawing(index) {
        const photoToDelete = this.item.response[index];
        if (!photoToDelete) {
          return;
        }

        AssetsPhotoStateService.deleteDrawing(photoToDelete);

        this.item.response.splice(index, 1, photoToDelete);
        this.$set(this.item, 'response', [...this.item.response]);
      },
      updateDrawing(index, drawing) {
        this.loading = true;

        if (!drawing) {
          this.removeDrawing(index);

          this.loading = false;
          return;
        }

        const photoToUpdate = this.item.response[index];

        AssetsPhotoStateService.updateDrawing(photoToUpdate, drawing);

        this.item.response.splice(index, 1, photoToUpdate);
        this.$set(this.item, 'response', [...this.item.response]);

        this.loading = false;
      },
      processUploadError(photoStub, error) {
        const errorMessage = t('Error loading image: {error}', { error });
        eventBus.$emit('error', errorMessage);

        const index = this.item.response.indexOf(photoStub);
        if (index !== -1) {
          this.item.response.splice(index, 1);
        }
      },
      async addPhoto(event) {
        if (!event.target.files) {
          return;
        }

        if (!this.item.response) {
          this.$set(this.item, 'response', []);
        }

        this.loading = true;

        // first iteration creates the loading placeholders
        for (let i = 0; i < event.target.files.length; i++) {
          const photoStub = getPhotoStub();
          this.item.response.push(photoStub);
          photoStubDictionary[i] = photoStub;
        }

        //this iteration uploads all
        for (let i = 0; i < event.target.files.length; i++) {
          const file = event.target.files[i];
          const photoStub = photoStubDictionary[i];
          if (!photoStub || !file) {
            continue;
          }

          try {
            const resizedPhoto = await PhotoReader.read(file);

            photoStub.thumbnail = resizedPhoto.thumbnail;
            photoStub.width = resizedPhoto.width;
            photoStub.height = resizedPhoto.height;
            AssetsPhotoStateService.addImage(photoStub, resizedPhoto.photo);

            this.$delete(photoStub, 'loading');
          } catch (error) {
            this.processUploadError(photoStub, error);
          }
        }

        this.$set(this.item, 'responded', this.item.response.length > 0);
        this.loading = false;
        event.target.value = null;
      },
    },
  };
</script>
